import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { BreakdownsContext } from './contexts/BreakdownsContext';
import { PieChart, PieChartDatum } from '../../../components/PieChart/PieChart';
import { BreakdownsContextType } from './contexts/BreakdownsContextTypes';
import { BreakdownsContainer } from '../../../components/BreakdownsContainer/BreakdownsContainer';
import { formatWithCustomGigaBillion } from '../../../utils/fomatNumbers';
import { mapCompromisedEntityTypes } from '../../../utils/actors';

interface HighRiskChartProps {
  narrative: any;
  mode?: 'actors' | 'publications';
  isClickable?: boolean;
  isShare?: boolean;
}

type ChartData = ChartDataItem[];

interface ChartDataItem {
  risk_type: string;
  actors_count: number;
  actors_share: number;
  publications_count: number;
  publications_share: number;
}

interface HighRiskData {
  total_audience: number;
  risk_distribution: Array<{
    risk_type: string;
    actors_count: number;
    actors_share: number;
    publications_count: number;
    publications_share: number;
  }>;
  top_actors: Array<{
    id: number;
    name: string;
    risk_type: string;
    country_name: string;
  }>;
}

export const HighRiskChart: React.FC<HighRiskChartProps> = ({
  mode = 'actors',
  isClickable = true,
  isShare = false,
}) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [actorsData, setActorsData] = useState<PieChartDatum[] | null>(null);
  const [publicationsData, setPublicationsData] = useState<PieChartDatum[] | null>(null);
  const [currentMode, setCurrentMode] = useState<'actors' | 'publications'>(
    mode,
  );
  const {
    highRisk,
    openModal,
    setCurrentWidgetName,
    setTypesFilter,
    setSubtypesFilter,
    setHighRiskExcludeFilter,
    setModalTitle,
  } = useContext(BreakdownsContext) as BreakdownsContextType;

  const mapEntitiesToColors = {
    INVOLVED_IN_INFLUENCE_OPERATIONS: '#FFC936',
    HACKED_OR_STOLEN: '#FF9736',
    SANCTIONS: '#FF3636',
    DISINFORMATION: '#A267DC',
  }

  const getSliceColor = (item: ChartDataItem) =>
    item.risk_type === null
      ? '#306DCF'
      : mapEntitiesToColors[item.risk_type as keyof typeof mapEntitiesToColors];

  const compromisedEntityTypes = mapCompromisedEntityTypes(t);

  const getChartDataByMode = (mode: 'actors' | 'publications'): PieChartDatum[] => {
    if (!chartData) {
      return [];
    }

    return chartData.map((item) => {
      const typeName = item.risk_type
        ? compromisedEntityTypes[
            item.risk_type as keyof typeof compromisedEntityTypes
          ].title
        : t('Other');
      const countMode =
        mode === 'actors' ? item.actors_count : item.publications_count;

      return {
        name: typeName,
        value: countMode,
        formattedCount: formatWithCustomGigaBillion(countMode),
        color: getSliceColor(item),
      };
    });
  };

  useEffect(() => {
    if (!highRisk) {
      return;
    }

    setChartData(highRisk?.risk_distribution || null);
  }, [highRisk]);

  useEffect(() => {
    if (chartData) {
      setActorsData(getChartDataByMode('actors'));
      setPublicationsData(getChartDataByMode('publications'));
    }
  }, [chartData]);

  const audienceCount = formatWithCustomGigaBillion(
    highRisk?.total_audience || 0,
  );
  const chartBadge =
    currentMode === 'actors'
      ? `${t('High risk audience:')} ${audienceCount}`
      : '';

  const breakdownsContainerProps: any = {
    title: t('High risk distribution'),
    onTabChange: (tabValue: 'actors' | 'publications') => {
      setCurrentMode(tabValue);
    },
    tabs: [
      { name: t('Actors'), value: 'actors' },
      { name: t('Publications'), value: 'publications' },
    ],
    flexGrow: 2,
  };

  return (
    <BreakdownsContainer {...breakdownsContainerProps}>
      <PieChart
        chartData={currentMode === 'actors' ? actorsData : publicationsData}
        isClickable={isClickable}
        onSelect={(index) => {
          if (index === null || !highRisk) return;

          const selectedRiskType = chartData
            ? chartData[index]?.risk_type
            : null;

          if (isClickable) {
            if (selectedRiskType === null) {
              setTypesFilter({
                value: 'discreditated',
                label: 'High-risk',
                bool: true,
              });
              setHighRiskExcludeFilter(true);
              setModalTitle(t('Other'));
            } else {
              setTypesFilter({
                value: 'discreditated',
                label: 'High risk accounts',
                bool: true,
              });
              setSubtypesFilter(selectedRiskType);
              setModalTitle(compromisedEntityTypes[selectedRiskType as keyof typeof compromisedEntityTypes]?.title);
            }
            setCurrentWidgetName('compromised_breakdown');
            openModal();
          }
        }}
        legendConfig={{
          position: 'bottom',
          textStyle: { fontSize: 14 },
        }}
        seriesName={currentMode === 'actors' ? t('Actors') : t('Publications')}
        chartBadge={chartBadge}
      />
    </BreakdownsContainer>
  );
};
